/**
 * top10-bootcamp-seed.ts
 *
 * Pre-populated Top 10 account plans sourced from:
 *   Mi6-Top10-Relationship-Sheet-populated.xlsx (bootcamp worksheet, Mi6 v3)
 *   Bootcamp-Notes-2026-04-28-to-30.pdf
 *   Mi6-Account-Planning-Rules-Matrix-v2.xlsx
 *
 * Rules:
 *   - bootcamp_seed fields are populated from the worksheet — read-mostly.
 *   - mpact fields are all blank (unanswered) — ae/bdr fills these in the platform.
 *   - opportunities[] is empty — opened once real discovery begins.
 *   - audit_log[] is empty — first entry is written when an ae claims the account.
 *   - rank_in_top10 reflects the order in the bootcamp worksheet.
 *   - IDs match Supabase account rows 36–45 (locked: Bose 36, BorgWarner 37...
 *     order in DB may differ from worksheet order — rank_in_top10 is the canonical rank).
 *
 * Worksheet columns → schema mapping:
 *   Column B  Company              → account_name
 *   Column C  Your Contact         → bootcamp_seed.relationship_map
 *   Column D  Your Relationship Story → bootcamp_seed.why_this_account
 *   Column E  Who We Want to Reach → bootcamp_seed.target_personas
 *   Column F  First Move           → bootcamp_seed.first_move
 *   Pain hypothesis                → derived from bootcamp notes + context
 *   Industry                       → inferred from company profile
 *
 * Unmapped / gap fields (see top10-plan-schema-2026-05-07.md for full analysis):
 *   - source_of_awareness: worksheet has no explicit column; populated from context.
 *   - competitive_intel: not in worksheet; left as "" placeholder.
 *   - historical_attempts: inferred from relationship story where available.
 *   - initial_pain_hypothesis: derived from bootcamp notes; not a worksheet column.
 */

import {
  AccountPlan,
  blankMpact,
  defaultDisqualification,
  RelationshipNode,
} from "@/types/account-plan";

// ---------------------------------------------------------------------------
// Helper: blank next_best_play placeholder
// ---------------------------------------------------------------------------
const blankNextBestPlay = () => ({
  play_type: "discover" as const,
  description: "",
  revenue_impact_estimate: 0,
  suggested_owner: "",
  suggested_date: "",
  auto_ranked: true,
  rank_score: 0,
});

// ---------------------------------------------------------------------------
// Helper: build a RelationshipNode with defaults
// ---------------------------------------------------------------------------
const contact = (
  name: string,
  role: string,
  org: string,
  owner: RelationshipNode["owner"],
  warmth: RelationshipNode["warmth"],
  notes: string
): RelationshipNode => ({
  name,
  role,
  org,
  owner,
  warmth,
  last_touch: "",
  notes,
});

// ---------------------------------------------------------------------------
// Shared metadata defaults
// ---------------------------------------------------------------------------
const NOW = new Date().toISOString();
const TENANT_ID = 1; // Partner tenant

// ---------------------------------------------------------------------------
// Account 1 — Stellantis
// Rank 1 in worksheet. Bryan's #1 OEM.
// ---------------------------------------------------------------------------
const stellantis: AccountPlan = {
  id: "top10-stellantis",
  account_id: "top10-stellantis",
  account_name: "Stellantis",
  industry: "OEM automotive",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Jacklyn in Purchasing is the ideal entry — stay inside Purchasing if we can. Bill Mazzara and Hassan Ridha are strong backups with an existing warm relationship. Separately, Bryan started an intro with Eric Bennington/Benton (ex-Stellantis/FCA procurement, now at TomTom) — a good intro meeting has already happened and the thread needs a follow-up to identify the right internal referral. Stellantis is Bryan's #1 OEM relationship.",
    initial_pain_hypothesis:
      "Large OEM with distributed manufacturing. Cost-savings initiative tracking is likely fragmented across plants — Profit+ addresses the formal program gap. Procurement-led entry creates a path to Finance and Operations who typically own cost-optimization mandates.",
    relationship_map: [
      contact(
        "Jacklyn",
        "Purchasing",
        "Stellantis",
        "coach",
        4,
        "Primary entry point. Keep engagement inside Purchasing initially."
      ),
      contact(
        "Bill Mazzara",
        "Purchasing (backup)",
        "Stellantis",
        "coach",
        4,
        "Known well by Bryan. Backup contact if Jacklyn track stalls."
      ),
      contact(
        "Hassan Ridha",
        "Purchasing (backup)",
        "Stellantis",
        "coach",
        4,
        "Known well by Bryan. Second backup in Purchasing."
      ),
      contact(
        "Eric Bennington/Benton",
        "ex-Stellantis/FCA Procurement — now at TomTom",
        "TomTom",
        "fox",
        3,
        "Intro meeting completed. Can recommend internal contacts at Stellantis or TomTom. Follow-up pending."
      ),
    ],
    source_of_awareness:
      "Warm relationship — Bryan's existing Purchasing network at Stellantis (FCA legacy).",
    competitive_intel: "",
    historical_attempts:
      "Existing professional relationship with Purchasing contacts. Eric Bennington thread is new (early 2026). No prior formal opportunity recorded.",
    first_move:
      "Follow-up on Eric Bennington thread + New Intro request via Jacklyn in Purchasing.",
    target_personas:
      "Procurement first (via Jacklyn). Then Finance and Operations leaders — the functions Profit+ typically sits under.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 1,
};

// ---------------------------------------------------------------------------
// Account 2 — Toyota
// ---------------------------------------------------------------------------
const toyota: AccountPlan = {
  id: "top10-toyota",
  account_id: "top10-toyota",
  account_name: "Toyota",
  industry: "OEM automotive",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Josh (CTO) is the primary engagement — the person Bryan has connected with most deeply at Toyota. Additional Toyota contacts exist in Bryan's contact list as backups, but Josh opens every door from the top. Toyota's Kaizen culture creates a natural alignment with Profit+ (a structured continuous improvement and cost-savings platform).",
    initial_pain_hypothesis:
      "Toyota runs Kaizen programs across its plants. The hypothesis is that cost-savings idea management is still informal or fragmented at the plant level, creating a gap that Profit+ fills with discipline, visibility, and traceability to the P&L.",
    relationship_map: [
      contact(
        "Josh",
        "Chief Technology Officer",
        "Toyota",
        "economic_buyer",
        4,
        "Main point of contact. CTO can route to Procurement, Finance, and Operations from the top. New Intro path confirmed."
      ),
    ],
    source_of_awareness:
      "Direct relationship — Bryan has engaged Josh (CTO) directly. PacCar discovery call surfaced Toyota/Kaizen context as relevant.",
    competitive_intel: "",
    historical_attempts:
      "Prior engagement with Josh at the CTO level. PacCar call (non-ICP) surfaced HR and Kaizen context, reinforcing Toyota fit.",
    first_move: "New Intro via Josh (CTO) — route to Procurement, Finance, and Operations leaders.",
    target_personas:
      "Josh routes to the three key department heads: Procurement, Finance, Operations.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 2,
};

// ---------------------------------------------------------------------------
// Account 3 — Aptiv
// Bryan's #1 Tier-1 relationship.
// ---------------------------------------------------------------------------
const aptiv: AccountPlan = {
  id: "top10-aptiv",
  account_id: "top10-aptiv",
  account_name: "Aptiv",
  industry: "Tier-1 automotive supplier",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Aptiv is Bryan's #1 Tier-1 relationship. Strongest personal connections are Bryan Whitten (CTO/CPSO) and Pankil. Kristie Pfosi (now Marelli CISO, personal friend) maintains a dotted-line influence at Aptiv and provides a second entry path via social/professional pull.",
    initial_pain_hypothesis:
      "Global Tier-1 supplier with distributed manufacturing. Cost-savings and efficiency initiative management across plants is likely fragmented — a Profit+ deployment could formalize and accelerate the program with full P&L traceability.",
    relationship_map: [
      contact(
        "Bryan Whitten",
        "CTO / Chief Product Security Officer (CPSO)",
        "Aptiv",
        "economic_buyer",
        5,
        "Primary target for new intro. Can connect to HR, Finance, and Operations."
      ),
      contact(
        "Pankil",
        "Senior technical leader",
        "Aptiv",
        "champion",
        5,
        "One of Bryan's strongest Aptiv relationships. Backup entry point."
      ),
      contact(
        "Kristie Pfosi",
        "CISO",
        "Marelli (formerly Aptiv)",
        "fox",
        5,
        "Personal friend — meets with Bryan regularly. Dotted-line influence still at Aptiv. Second entry path."
      ),
    ],
    source_of_awareness:
      "Long-standing personal relationship — Bryan's deepest Tier-1 automotive connection.",
    competitive_intel: "",
    historical_attempts:
      "Deep ongoing relationship. No prior formal Profit+ opportunity. Kristie Pfosi path spans Aptiv → Marelli as she moved companies.",
    first_move:
      "New Intro via Bryan Whitten (CTO) + Follow-up via Kristie Pfosi channel.",
    target_personas:
      "Bryan Whitten connects to HR, Finance, and Operations. Kristie provides the secondary social path.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 3,
};

// ---------------------------------------------------------------------------
// Account 4 — Marelli
// ---------------------------------------------------------------------------
const marelli: AccountPlan = {
  id: "top10-marelli",
  account_id: "top10-marelli",
  account_name: "Marelli",
  industry: "Tier-1 automotive supplier",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Kristie Pfosi (CISO, Marelli) is a personal friend of Bryan's. Long-standing relationship across multiple companies (Karamba → Stellantis → Mitsubishi Electric → Aptiv → Delta Faucet → now Marelli). The personal connection gives a warm, trusted entry path inside Marelli.",
    initial_pain_hypothesis:
      "Marelli is a globally distributed Tier-1 supplier following recent M&A activity. Cost-savings program formalization and idea management across plants is likely a gap — Profit+ addresses this directly.",
    relationship_map: [
      contact(
        "Kristie Pfosi",
        "Chief Information Security Officer (CISO)",
        "Marelli",
        "champion",
        5,
        "Personal friend. Long multi-company history with Bryan. Briefly discussed Profit+ — no detail yet. Follow-up needed."
      ),
    ],
    source_of_awareness:
      "Personal friendship — Kristie Pfosi and Bryan have maintained a close relationship across multiple career moves.",
    competitive_intel: "",
    historical_attempts:
      "Brief informal discussion with Kristie. No formal opportunity opened yet.",
    first_move:
      "Hybrid — brief prior discussion, no detail yet. Follow-up conversation with Kristie to open formal exploration.",
    target_personas:
      "Kristie connects the team to Procurement, Finance, and Operations inside Marelli.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 4,
};

// ---------------------------------------------------------------------------
// Account 5 — Ford
// ---------------------------------------------------------------------------
const ford: AccountPlan = {
  id: "top10-ford",
  account_id: "top10-ford",
  account_name: "Ford",
  industry: "OEM automotive",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Multiple contacts at different levels inside Ford. Tim Kearney (primary — recently retired, still reachable) and Mike Westra (active inside contact). Different levels of access allow multi-threaded entry.",
    initial_pain_hypothesis:
      "Large OEM with many manufacturing plants. Decentralized cost-savings idea management is a known gap in organizations of this scale. Profit+ creates the formal program infrastructure Ford's operations teams need.",
    relationship_map: [
      contact(
        "Tim Kearney",
        "Retired Ford executive",
        "Ford (formerly)",
        "fox",
        4,
        "Primary contact — recently retired, still reachable. Deep institutional knowledge of Ford's procurement and ops structure."
      ),
      contact(
        "Mike Westra",
        "Active inside contact",
        "Ford",
        "coach",
        3,
        "Active employee. Current inside path into Ford operations."
      ),
    ],
    source_of_awareness:
      "Existing professional relationships with both Tim Kearney and Mike Westra.",
    competitive_intel: "",
    historical_attempts: "Existing contact at multiple levels — no prior formal opportunity.",
    first_move:
      "New Intro via Tim Kearney and Mike Westra — route to HR, Technical/Ops, and Finance.",
    target_personas: "HR, Technical/Operations, Finance — same three-persona pattern as other OEMs.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 5,
};

// ---------------------------------------------------------------------------
// Account 6 — Bosch
// ---------------------------------------------------------------------------
const bosch: AccountPlan = {
  id: "top10-bosch",
  account_id: "top10-bosch",
  account_name: "Bosch",
  industry: "Tier-1 automotive supplier / industrial technology",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Robert Kaiser is Bosch's global head of cybersecurity and holds additional ceremonial positions. Bryan has known Robert for over 10 years — one of his go-to automotive cybersecurity advisors. Long relationship that never converted to a deal, but trust and access are established.",
    initial_pain_hypothesis:
      "Global industrial technology company with thousands of manufacturing sites. Formal cost-savings program management at scale is a natural pain hypothesis. Robert's cybersecurity mandate also surfaces a security pre-clearance conversation that Profit+ can navigate.",
    relationship_map: [
      contact(
        "Robert Kaiser",
        "Head of Cybersecurity, Bosch (global) + additional ceremonial roles",
        "Bosch",
        "fox",
        4,
        "10+ year relationship. Go-to automotive cybersecurity advisor. Multiple past near-deals that did not close. First-move introduction request."
      ),
    ],
    source_of_awareness:
      "Long-standing personal relationship from Bryan's early days in automotive cybersecurity.",
    competitive_intel: "",
    historical_attempts:
      "Multiple projects together over 10+ years that 'never quite panned out' — close relationship maintained throughout. No prior Profit+ opportunity.",
    first_move: "First-move introduction — re-engage Robert Kaiser to request referrals to the three key department heads.",
    target_personas:
      "Robert Kaiser provides names of the three key department heads (Procurement, Finance, Operations).",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 6,
};

// ---------------------------------------------------------------------------
// Account 7 — Bose
// Private company — easier to navigate.
// ---------------------------------------------------------------------------
const bose: AccountPlan = {
  id: "top10-bose",
  account_id: "top10-bose",
  account_name: "Bose",
  industry: "Industrial / consumer electronics manufacturing",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Will Robinson spent over 25 years at Bose and remains directly connected. As a private company, Bose is easier to navigate — fewer procurement layers and more direct access to decision makers. Will (now at another company) can facilitate introductions to current Bose leadership.",
    initial_pain_hypothesis:
      "Private manufacturing company. Formal cost-savings initiative management is unlikely to be systematized at the scale Profit+ enables — especially given Bose's manufacturing-adjacent operations footprint.",
    relationship_map: [
      contact(
        "Will Robinson",
        "Former Bose employee (25+ years tenure, now elsewhere)",
        "Bose (formerly)",
        "fox",
        3,
        "No longer at Bose but closely connected after 25 years. Bryan will reach out to Will at his current company to request internal introductions."
      ),
    ],
    source_of_awareness:
      "Professional relationship with Will Robinson, long-tenured Bose insider.",
    competitive_intel: "",
    historical_attempts:
      "No prior formal engagement with Bose. Will Robinson connection is the activation path.",
    first_move:
      "Reach out to Will Robinson (at his current company) to request introductions inside Bose.",
    target_personas: "Whoever Will Robinson recommends — expected path to Procurement, Finance, Operations.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 7,
};

// ---------------------------------------------------------------------------
// Account 8 — Lear
// ---------------------------------------------------------------------------
const lear: AccountPlan = {
  id: "top10-lear",
  account_id: "top10-lear",
  account_name: "Lear",
  industry: "Tier-1 automotive supplier",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "André Weimer Schmidt — former head of all cybersecurity at Lear, now COO at Block Harbor Security. Bryan helped position André for the COO role. André is considered the 'godfather of automotive cybersecurity.' Separately, Bryan knows Zack and Lars at Lear, and has had a recent brief introduction with Isaac (formerly Lear, now at Stoneridge — Stoneridge to be added to list).",
    initial_pain_hypothesis:
      "Large Tier-1 supplier with global manufacturing. Cost-savings program management is likely fragmented across plants. Lear's scale makes a formal Profit+ deployment high-value.",
    relationship_map: [
      contact(
        "André Weimer Schmidt",
        "Formerly: Head of Cybersecurity at Lear. Now: COO at Block Harbor Security",
        "Block Harbor Security (formerly Lear)",
        "fox",
        5,
        "Deep trust relationship — Bryan helped André secure the COO role. 'Godfather' of Lear's automotive cybersecurity. Primary path back into Lear via referral."
      ),
      contact(
        "Zack",
        "Lear employee (specific role TBD)",
        "Lear",
        "coach",
        3,
        "Known contact inside Lear. Details to confirm."
      ),
      contact(
        "Lars",
        "Lear employee (specific role TBD)",
        "Lear",
        "coach",
        3,
        "Known contact inside Lear. Details to confirm."
      ),
      contact(
        "Isaac",
        "Formerly Lear — now at Stoneridge",
        "Stoneridge",
        "fox",
        3,
        "Brief introduction a few weeks ago. Isaac moved from Lear to Stoneridge — both accounts worth pursuing. Stoneridge to be added to target list."
      ),
    ],
    source_of_awareness:
      "Long-term professional relationship with André Weimer Schmidt spanning Lear cybersecurity era.",
    competitive_intel: "",
    historical_attempts:
      "Ongoing professional relationship with André. Recent brief intro with Isaac. No prior formal Profit+ opportunity at Lear.",
    first_move:
      "Follow-up — reach out to André for an introduction to someone at Lear. Also follow up with Isaac (Stoneridge thread).",
    target_personas:
      "André recommends internal Lear contacts. Also anyone Isaac surfaces at Stoneridge.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 8,
};

// ---------------------------------------------------------------------------
// Account 9 — Rivian
// ---------------------------------------------------------------------------
const rivian: AccountPlan = {
  id: "top10-rivian",
  account_id: "top10-rivian",
  account_name: "Rivian",
  industry: "EV / new entrant OEM",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Trevor Pickrell and Jenna Peltier are both engineers at Rivian, formerly at FCA. Personal/family connections to Bryan's network — keeping the engagement professional to avoid awkwardness. Bryan has also had brief conversations with Rivian's CTO. Strong personal warmth but professional discipline required.",
    initial_pain_hypothesis:
      "Fast-growing EV OEM building manufacturing from scratch. Cost-savings initiative management processes are likely immature or informal — high opportunity for Profit+ to establish the discipline early in the company's manufacturing maturity curve.",
    relationship_map: [
      contact(
        "Trevor Pickrell",
        "Engineer (formerly FCA)",
        "Rivian",
        "coach",
        4,
        "Family connection. Former FCA colleague. Keeping professional. Can facilitate introductions inside Rivian."
      ),
      contact(
        "Jenna Peltier",
        "Engineer (formerly FCA)",
        "Rivian",
        "coach",
        4,
        "Family connection. Former FCA colleague. Keeping professional. Second internal path."
      ),
    ],
    source_of_awareness:
      "Personal/family connections to Trevor Pickrell and Jenna Peltier, both formerly FCA.",
    competitive_intel: "",
    historical_attempts:
      "Brief conversations with Rivian's CTO. Personal relationships with two engineering-level contacts.",
    first_move:
      "Follow-up via Trevor Pickrell and Jenna Peltier — identify the key decision maker contacts they recommend in the three target positions.",
    target_personas:
      "Anyone Trevor or Jenna identify in key positions. CTO is a known contact for further engagement.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 9,
};

// ---------------------------------------------------------------------------
// Account 10 — BorgWarner
// ---------------------------------------------------------------------------
const borgwarner: AccountPlan = {
  id: "top10-borgwarner",
  account_id: "top10-borgwarner",
  account_name: "BorgWarner",
  industry: "Tier-1 automotive supplier / powertrain",
  icp_confirmed: true,
  tenant_id: TENANT_ID,
  owner_id: "",
  bdr_id: "",
  bootcamp_seed: {
    why_this_account:
      "Davy Schlosser — warm relationship from Bryan's days at Karamba. Worked on projects together. The relationship is described as warm, not deep — 'don't overstay it.' New introduction or request-for-intro is the right first move.",
    initial_pain_hypothesis:
      "Global powertrain and propulsion Tier-1 supplier. Multi-plant operations globally. Cost-savings initiative management across sites is a natural Profit+ fit — especially as BorgWarner accelerates its EV product transition.",
    relationship_map: [
      contact(
        "Davy Schlosser",
        "BorgWarner executive (role TBD — confirm at outreach)",
        "BorgWarner",
        "fox",
        3,
        "Warm relationship from Karamba days. Multiple past projects together. Relationship is warm but should not be overplayed. New introduction or request-for-intro appropriate."
      ),
    ],
    source_of_awareness:
      "Professional relationship from Bryan's Karamba (automotive cybersecurity) days.",
    competitive_intel: "",
    historical_attempts:
      "Past project collaboration at Karamba. No prior formal Profit+ opportunity.",
    first_move:
      "New introduction request or request for intro via Davy Schlosser — ask for names of the three key department heads.",
    target_personas:
      "Whoever Davy Schlosser identifies — Procurement, Finance, Operations leads.",
  },
  mpact: blankMpact(),
  opportunities: [],
  next_best_play: blankNextBestPlay(),
  disqualification: defaultDisqualification(),
  audit_log: [],
  created_at: NOW,
  updated_at: NOW,
  last_activity_at: NOW,
  rank_in_top10: 10,
};

// ---------------------------------------------------------------------------
// Exported canonical Top 10 — ordered by rank_in_top10
// ---------------------------------------------------------------------------
export const TOP10_BOOTCAMP_SEED: AccountPlan[] = [
  stellantis,   // rank 1
  toyota,       // rank 2
  aptiv,        // rank 3
  marelli,      // rank 4
  ford,         // rank 5
  bosch,        // rank 6
  bose,         // rank 7
  lear,         // rank 8
  rivian,       // rank 9
  borgwarner,   // rank 10
];

export default TOP10_BOOTCAMP_SEED;
